import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/layout/Page";
import { TripCard, TripCardSkeleton } from "@/components/TripCard";
import { useBookings } from "@/hooks/queries";

const FILTERS = [
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
  { id: "all", label: "All" },
];

function EmptyState({ filter }) {
  return (
    <div className="lux-card rise flex flex-col items-center px-6 py-14 text-center" data-testid="trips-empty">
      <CalendarPlus className="h-8 w-8 text-gold" strokeWidth={1.4} />
      <h3 className="mt-4 text-2xl text-ivory">{filter === "past" ? "No past rides yet" : "Your itinerary is clear"}</h3>
      <p className="mt-2 max-w-xs text-sm text-mist">Reserve a chauffeur and your journeys will be kept here.</p>
      <Link to="/book" className="btn-gold mt-6" data-testid="trips-empty-book-btn">Book a Ride</Link>
    </div>
  );
}

export default function Trips() {
  const [filter, setFilter] = useState("upcoming");
  const { data = [], isLoading, isError, isFetching, refetch } = useBookings(filter);
  return (
    <div data-testid="trips-page">
      <PageHeader
        eyebrow="Itinerary"
        title="Your trips"
        aside={
          <button type="button" onClick={() => refetch()} className="btn-ghost h-11 w-11 p-0" title="Refresh" data-testid="trips-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin text-gold" : ""}`} />
          </button>
        }
      />
      <div className="rise stagger-1 mb-6 flex gap-2" data-testid="trips-filters">
        {FILTERS.map((f) => (
          <button key={f.id} type="button" className="chip" data-active={filter === f.id} onClick={() => setFilter(f.id)} data-testid={`trips-filter-${f.id}`}>
            {f.label}
          </button>
        ))}
      </div>
      <div className="space-y-3" data-testid="trips-list">
        {isLoading && [0, 1, 2].map((i) => <TripCardSkeleton key={i} />)}
        {isError && (
          <div className="lux-card p-6 text-sm text-mist" data-testid="trips-error">
            We couldn't load your trips.{" "}
            <button type="button" onClick={() => refetch()} className="text-gold underline" data-testid="trips-retry-btn">Try again</button>
          </div>
        )}
        {!isLoading && !isError && data.length === 0 && <EmptyState filter={filter} />}
        {data.map((b, i) => <TripCard key={b.id} booking={b} index={i} />)}
      </div>
    </div>
  );
}
